/**
 * CharacterAsset manifest — the single source of truth for how a
 * character (AI opponent or the hero) is visually represented in V2.
 *
 * Swap-out contract (see docs/design/M9-visual-system.md):
 *   1. Drop a new SVG / PNG into `src/assets/characters/<codename>.svg`.
 *   2. Optionally add per-expression variants (e.g. `void-thinking.svg`).
 *   3. Update the `sprites` entry below to point at the new URLs.
 *   4. No changes needed in engine / state / hooks / routes — the avatar
 *      component reads this manifest exclusively.
 *
 * The `idle` sprite is required; other expressions fall back to it.
 */

import grimSprite from '@/assets/characters/grim.svg?url';
import heroSprite from '@/assets/characters/hero.svg?url';
import jestSprite from '@/assets/characters/jest.svg?url';
import oracleSprite from '@/assets/characters/oracle.svg?url';
import paleSprite from '@/assets/characters/pale.svg?url';
import voidSprite from '@/assets/characters/void.svg?url';
import type { PersonalityName } from '@/gto/types';

export type CharacterCodename = PersonalityName | 'HERO';

export type CharacterExpression = 'idle' | 'thinking' | 'folded' | 'allin' | 'winner';

export type CharacterAsset = {
  codename: CharacterCodename;
  /** Display string in UI (kept uppercase for pixel-arcade tone). */
  displayName: string;
  /** One-line flavor for home-screen lineup / tooltips. */
  tagline: string;
  /** Tailwind utility class for the palette anchor (`bg-char-void` etc.). */
  swatchClass: string;
  /** CSS custom property name (without the `--` prefix) for the glow hue.
   * Used for inline-style glow rings without triggering Tailwind JIT
   * safelist issues on dynamic alpha. */
  glowVar: string;
  /**
   * Sprite URLs keyed by expression. Only `idle` is required.
   * Missing expressions fall back to `idle` in `resolveSprite`.
   */
  sprites: {
    idle: string;
    thinking?: string;
    folded?: string;
    allin?: string;
    winner?: string;
  };
};

export const CHARACTER_ASSETS: Readonly<Record<CharacterCodename, CharacterAsset>> = {
  VOID: {
    codename: 'VOID',
    displayName: 'VOID',
    tagline: '우주를 응시하는 고요한 관찰자',
    swatchClass: 'bg-char-void',
    glowVar: 'color-char-void',
    sprites: { idle: voidSprite },
  },
  GRIM: {
    codename: 'GRIM',
    displayName: 'GRIM',
    tagline: '건파우더 향기의 해골 카우보이',
    swatchClass: 'bg-char-grim',
    glowVar: 'color-char-grim',
    sprites: { idle: grimSprite },
  },
  PALE: {
    codename: 'PALE',
    displayName: 'PALE',
    tagline: '흘러가다 멈춰 서는 창백한 잔상',
    swatchClass: 'bg-char-pale',
    glowVar: 'color-char-pale',
    sprites: { idle: paleSprite },
  },
  JEST: {
    codename: 'JEST',
    displayName: 'JEST',
    tagline: '깨진 미소로 판을 흔드는 광대',
    swatchClass: 'bg-char-jest',
    glowVar: 'color-char-jest',
    sprites: { idle: jestSprite },
  },
  ORACLE: {
    codename: 'ORACLE',
    displayName: 'ORACLE',
    tagline: '확률만이 진실이라 믿는 예언자',
    swatchClass: 'bg-char-oracle',
    glowVar: 'color-char-oracle',
    sprites: { idle: oracleSprite },
  },
  HERO: {
    codename: 'HERO',
    displayName: 'YOU',
    tagline: '이 테이블의 여섯 번째 플레이어 — 당신',
    swatchClass: 'bg-char-hero',
    glowVar: 'color-char-hero',
    sprites: { idle: heroSprite },
  },
};

export function getCharacterAsset(codename: CharacterCodename): CharacterAsset {
  return CHARACTER_ASSETS[codename];
}

export function resolveSprite(asset: CharacterAsset, expr: CharacterExpression): string {
  return asset.sprites[expr] ?? asset.sprites.idle;
}

/** Ordered list for lineup rendering (home / lobby). Hero first, then the
 *  5 AI in the canonical seat order. */
export const LINEUP_ORDER: ReadonlyArray<CharacterCodename> = [
  'HERO',
  'VOID',
  'GRIM',
  'PALE',
  'JEST',
  'ORACLE',
];
